import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import "./AdminPanel.css";
import AdminSidebar from "./AdminSidebar";
import AdministratorSidebar from "./AdministratorSidebar";
import UserSidebar from "./UserSidebar";
import "bootstrap/dist/css/bootstrap.min.css";

const VehiclesCost = () => {
    const navigate = useNavigate();
    const [userType, setUserType] = useState("");
    const [userName, setUserName] = useState("");
    const [userFullName, setUserFullName] = useState("");
    const [stockIds, setStockIds] = useState([]);
    const [selectedStockId, setSelectedStockId] = useState("");
    const [formData, setFormData] = useState({
        vin: "",
        buying_amount: "",
        auction_cost: "",
        transport: "",
        inspection: "",
        freight: "",
        total_cost: ""
    });
    const [error, setError] = useState("");
    const [success, setSuccess] = useState("");

    useEffect(() => {
        // User authentication
        const storedUserType = localStorage.getItem("usertype");
        const storedUserName = localStorage.getItem("userName");
        const storedFName = localStorage.getItem("fname");
        const storedLName = localStorage.getItem("lname");

        if (!storedUserType) {
            navigate("/login");
        } else {
            setUserType(storedUserType);
            setUserName(storedUserName);
            if (storedFName && storedLName) {
                setUserFullName(`${storedFName} ${storedLName}`);
            } else if (storedFName) {
                setUserFullName(storedFName);
            } else {
                setUserFullName(storedUserName);
            }
        }

        // Fetch stock IDs
        fetchStockIds();
    }, [navigate]);

    const fetchStockIds = async () => {
        try {
            const response = await fetch("https://api.saadmotors.com/vehicle_cost.php?action=get_stock_ids");
            const data = await response.json();
            if (data.success) {
                setStockIds(data.data);
            } else {
                setError(data.message);
            }
        } catch (err) {
            setError("Failed to fetch stock IDs");
        }
    };

    const fetchVehicleData = async (stockId) => {
        try {
            const response = await fetch(`https://api.saadmotors.com/vehicle_cost.php?action=get_vehicle&stock_id=${stockId}`);
            const data = await response.json();
            if (data.success) {
                setFormData({
                    vin: data.data.vin || "",
                    buying_amount: data.data.buying_amount || "",
                    auction_cost: data.data.auction_cost || "",
                    transport: data.data.transport || "",
                    inspection: data.data.inspection || "",
                    freight: data.data.freight || "",
                    total_cost: calculateTotalCost(data.data)
                });
                setError("");
            } else {
                setError(data.message);
            }
        } catch (err) {
            setError("Failed to fetch vehicle data");
        }
    };

    const calculateTotalCost = (data) => {
        const costs = [
            parseFloat(data.buying_amount || 0),
            parseFloat(data.auction_cost || 0),
            parseFloat(data.transport || 0),
            parseFloat(data.inspection || 0),
            parseFloat(data.freight || 0)
        ];
        return costs.reduce((sum, cost) => sum + cost, 0).toFixed(2);
    };

    const handleStockIdChange = (e) => {
        const stockId = e.target.value;
        setSelectedStockId(stockId);
        if (stockId) {
            fetchVehicleData(stockId);
        } else {
            setFormData({
                vin: "",
                buying_amount: "",
                auction_cost: "",
                transport: "",
                inspection: "",
                freight: "",
                total_cost: ""
            });
        }
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => {
            const newData = { ...prev, [name]: value };
            newData.total_cost = calculateTotalCost(newData);
            return newData;
        });
    };

    const handleUpdate = async () => {
        if (!selectedStockId) {
            setError("Please select a stock ID");
            return;
        }

        try {
            const response = await fetch("https://api.saadmotors.com/vehicle_cost.php?action=update", {
                method: "POST",
                headers: {
                    "Content-Type": "application/json"
                },
                body: JSON.stringify({
                    stock_id: selectedStockId,
                    ...formData
                })
            });
            const data = await response.json();
            if (data.success) {
                setSuccess("Costs updated successfully");
                setError("");
            } else {
                setError(data.message);
            }
        } catch (err) {
            setError("Failed to update costs");
        }
    };

    const logout = () => {
        localStorage.removeItem("token");
        localStorage.removeItem("email");
        localStorage.removeItem("userName");
        localStorage.removeItem("usertype");
        localStorage.removeItem("fname");
        localStorage.removeItem("lname");
        navigate("/login");
    };

    return (
        <div className="admin-container">
            <div className="sidebar-container">
                {userType === "administrator" && <AdministratorSidebar />}
                {userType === "admin" && <AdminSidebar />}
                {userType === "user" && <UserSidebar />}
            </div>

            <div className="admin-content">
                <div className="adminnavbar">
                    <h1>Welcome to the Admin Panel, {userFullName}</h1>
                    <button onClick={logout} className="logoutbtn btn btn-danger">
                        Logout
                    </button>
                </div>

                <div className="mainsection container mt-4">
                    <h3>Vehicles Cost</h3>

                    {error && <div className="alert alert-danger">{error}</div>}
                    {success && <div className="alert alert-success">{success}</div>}

                    <div className="mb-4">
                        <label htmlFor="stockId" className="form-label">Select Stock ID</label>
                        <select
                            id="stockId"
                            className="form-select"
                            value={selectedStockId}
                            onChange={handleStockIdChange}
                        >
                            <option value="">Select Stock ID</option>
                            {stockIds.map(stock => (
                                <option key={stock.stock_id} value={stock.stock_id}>
                                    {stock.stock_id}
                                </option>
                            ))}
                        </select>
                    </div>

                    <div className="row g-3">
                        {[
                            { label: "VIN", name: "vin" },
                            { label: "Buying Amount", name: "buying_amount", type: "number" },
                            { label: "Auction Cost", name: "auction_cost", type: "number" },
                            { label: "Transport", name: "transport", type: "number" },
                            { label: "Inspection", name: "inspection", type: "number" },
                            { label: "Freight", name: "freight", type: "number" },
                            { label: "Total Cost", name: "total_cost", type: "number", readOnly: true }
                        ].map(field => (
                            <div key={field.name} className="col-md-6">
                                <label htmlFor={field.name} className="form-label">{field.label}</label>
                                <input
                                    type={field.type || "text"}
                                    className="form-control"
                                    id={field.name}
                                    name={field.name}
                                    value={formData[field.name]}
                                    onChange={handleInputChange}
                                    readOnly={field.readOnly}
                                    disabled={!selectedStockId}
                                />
                            </div>
                        ))}
                    </div>

                    <button
                        className="btn btn-primary mt-4"
                        onClick={handleUpdate}
                        disabled={!selectedStockId}
                    >
                        Update Costs
                    </button>
                </div>
            </div>
        </div>
    );
};

export default VehiclesCost;